import React, { useRef, useEffect } from 'react'
import { useCampaigns, FILTER_OPTIONS } from '../../contexts/CampaignsContext'
import { usePagination } from '../../hooks/usePagination'
import Pagination from '../Pagination'
import AccountDataSection from './AccountDataSection'

const AdsDashboard = ({ onRefresh }) => {
  const accountSectionRef = useRef(null)

  const {
    campaigns,
    loading,
    selectedPlatform,
    selectedAccount,
    selectPlatform,
    selectAccount,
    getPlataformColor,
    formatCurrency,
    formatNumber,
    formatDateOnly
  } = useCampaigns()

  const {
    paginatedData: paginatedMetrics,
    currentPage,
    totalPages,
    totalItems,
    goToPage
  } = usePagination(campaigns, 10, true)


  // 🎯 EFECTO para hacer scroll cuando se selecciona una cuenta
  useEffect(() => {
    if (selectedAccount && accountSectionRef.current) {
      const timer = setTimeout(() => {
        accountSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        })
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [selectedAccount])

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Controls */}
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0'>
        <div className='flex items-center space-x-4'>
          <select
            value={selectedPlatform}
            onChange={(e) => selectPlatform(e.target.value)}
            className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            {FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className='flex items-center space-x-3'>
          <button
            onClick={onRefresh}
            disabled={loading}
            className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2'
          >
            <span>🔄</span>
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Campaign Performance Table */}
      
      {paginatedMetrics && paginatedMetrics.length > 0 && (
        <div className='bg-white rounded-lg'>
          <div className='py-4'>
            <h3 className='text-lg font-semibold text-gray-900'>
              📈 Rendimiento de Campañas (Hoy)
            </h3>
            <span className='text-gray-400 text-sm'>
              * Selecciona una cuenta para ver los datos.
            </span>
          </div>
          <div className='overflow-x-auto rounded-lg border border-gray-200'>
            <div className='rounded-lg'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Nombre de la Cuenta
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Nombre Cliente
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Plataforma
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Nombre de Campaña
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Activo
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Alcance
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Frecuencia
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Costo por Resultado
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Importe Gastado
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      CTR
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Presupuesto
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Fecha de Creación
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Fecha de Última Modificación
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {paginatedMetrics.map((metric, index) => (
                    <tr
                      key={metric.id || index}
                      className={`h-16 ${
                        metric.account_name ? 'hover:bg-gray-100 cursor-pointer' : ''
                      }`}
                      onClick={() => {
                        if (metric.account_name) selectAccount(metric.account_id)
                      }}
                    >
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900 max-w-80 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none]'>
                        {metric.account_name || ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900 max-w-80 text-ellipsis overflow-hidden'>
                        {metric.client_name || ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.platform ? (
                          <span
                            className={`inline-block px-2 py-1 rounded-full font-semibold ${getPlataformColor(
                              metric.platform
                            )}`}
                          >
                            {metric.platform}
                          </span>
                        ) : (
                          ''
                        )}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.campaign_name || ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.active !== undefined ? (metric.active ? 'Sí' : 'No') : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.reach !== undefined ? formatNumber(metric.reach) : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.frequency !== undefined ? metric.frequency.toFixed(2) : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.cost_per_result !== undefined
                          ? formatCurrency(metric.cost_per_result)
                          : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.cost_micros !== undefined
                          ? formatCurrency(metric.cost_micros / 1000000)
                          : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.ctr !== undefined ? metric.ctr.toFixed(2) + '%' : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.budget !== undefined ? formatCurrency(metric.budget) : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.created_at ? formatDateOnly(metric.created_at) : ''}
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {metric.updated_at ? formatDateOnly(metric.updated_at) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Paginacion*/}
      <div>
        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={10}
            totalItems={totalItems}
          />
        )}
      </div>

      {/* Sin datos */}
      {!totalItems && !loading && (
        <div className='text-center py-12'>
          <div className='text-gray-400 text-6xl mb-4'>📊</div>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>No hay datos disponibles</h3>
          <p className='text-gray-500 mb-4'>
            Configura y conecta tus cuentas de Google Ads para ver las métricas en tiempo real.
          </p>
          <button
            onClick={() => onRefresh()}
            className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700'
          >
            Configurar Cuentas
          </button>
        </div>
      )}

      {selectedAccount && (
        <div className='border-t border-gray-200 pt-6'>
          <div className='flex items-center space-x-2 mb-4'>
            <div className='h-2 w-2 bg-blue-500 rounded-full animate-pulse'></div>
            <h2 className='text-xl font-bold text-gray-900'>Detalles de la Cuenta Seleccionada</h2>
          </div>
        </div>
      )}

      {selectedAccount && (
        <div ref={accountSectionRef} className='scroll-mt-4'>
          <AccountDataSection />
        </div>
      )}
    </div>
  )
}

export default AdsDashboard
